/**
 * EncryptionService - Web Crypto API wrapper for secure content encryption
 *
 * Provides secure encryption/decryption using AES-GCM 256-bit encryption.
 * Uses key derivation (PBKDF2 and HKDF) to create hierarchical keys.
 *
 * Key Hierarchy:
 * - Master Key: Derived from user credentials (userId + password/session)
 * - Book Key: Derived from master key + bookId
 * - Chunk Key: Derived from book key + chunk index (optional)
 *
 * Security Features:
 * - AES-GCM 256-bit encryption
 * - Unique IV per chunk
 * - PBKDF2 for password derivation (100k iterations)
 * - HKDF for key derivation
 * - No plaintext storage
 */

import type { EncryptedData, DecryptionKey, KeyDerivationParams } from '@/types/offline';

export class EncryptionService {
  private readonly algorithm = 'AES-GCM';
  private readonly keyLength = 256;
  private readonly pbkdf2Iterations = 100000;
  private readonly ivLength = 12; // 96 bits for GCM

  // Cache for derived keys (in-memory only, cleared on logout)
  private keyCache: Map<string, CryptoKey> = new Map();

  /**
   * Derive master key from user credentials
   * Uses PBKDF2 with high iteration count for security
   */
  async deriveMasterKey(userId: string, password: string, salt?: ArrayBuffer): Promise<CryptoKey> {
    const cacheKey = `master-${userId}`;

    // Check cache first
    if (this.keyCache.has(cacheKey)) {
      return this.keyCache.get(cacheKey)!;
    }

    // Generate deterministic salt from userId if not provided
    // This ensures the same user always gets the same encryption key
    let keySalt: Uint8Array;
    if (salt) {
      keySalt = new Uint8Array(salt);
    } else {
      // Derive salt from userId deterministically
      const encoder = new TextEncoder();
      const userIdBytes = encoder.encode(`salt-${userId}`);
      const hashBuffer = await crypto.subtle.digest('SHA-256', userIdBytes);
      keySalt = new Uint8Array(hashBuffer).slice(0, 16); // Use first 16 bytes as salt
    }

    // Import password as key material
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    // Derive master key using PBKDF2
    const masterKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: keySalt,
        iterations: this.pbkdf2Iterations,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: this.algorithm, length: this.keyLength },
      true, // ✅ Must be extractable for HKDF key derivation
      ['encrypt', 'decrypt']
    );

    // Cache the key
    this.keyCache.set(cacheKey, masterKey);

    return masterKey;
  }

  /**
   * Derive book-specific key from master key
   * Uses HKDF for key derivation
   */
  async deriveBookKey(masterKey: CryptoKey, bookId: string): Promise<DecryptionKey> {
    const cacheKey = `book-${bookId}`;

    // Check cache first
    if (this.keyCache.has(cacheKey)) {
      return {
        key: this.keyCache.get(cacheKey)!,
        keyId: cacheKey,
      };
    }

    // Use bookId as info parameter for HKDF
    const encoder = new TextEncoder();
    const info = encoder.encode(`book-${bookId}`);

    // Export master key to raw format for HKDF
    const masterKeyRaw = await crypto.subtle.exportKey('raw', masterKey);

    // Import as HKDF key
    const hkdfKey = await crypto.subtle.importKey(
      'raw',
      masterKeyRaw,
      'HKDF',
      false,
      ['deriveKey']
    );

    // Derive book key using HKDF
    const bookKey = await crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        salt: new Uint8Array(16), // Can use empty salt since we have strong master key
        info: info,
        hash: 'SHA-256',
      },
      hkdfKey,
      { name: this.algorithm, length: this.keyLength },
      false,
      ['encrypt', 'decrypt']
    );

    // Cache the key
    this.keyCache.set(cacheKey, bookKey);

    return {
      key: bookKey,
      keyId: cacheKey,
    };
  }

  /**
   * Derive chunk-specific key from book key (optional - for extra security)
   * Can be used to have unique keys per chapter/chunk
   */
  async deriveChunkKey(bookKey: CryptoKey, chunkIndex: number): Promise<CryptoKey> {
    const cacheKey = `chunk-${chunkIndex}`;

    // For performance, we may skip this and use bookKey directly
    // Uncomment if you want per-chunk keys

    return bookKey; // Simplified: Use book key directly
  }

  /**
   * Encrypt content using AES-GCM
   */
  async encrypt(content: ArrayBuffer, key: CryptoKey): Promise<EncryptedData> {
    // Generate random IV for this encryption
    const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));

    // Encrypt content
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: this.algorithm,
        iv: iv,
      },
      key,
      content
    );

    return {
      data: encryptedData,
      iv: iv,
    };
  }

  /**
   * Decrypt content using AES-GCM
   */
  async decrypt(encryptedData: EncryptedData, key: CryptoKey): Promise<ArrayBuffer> {
    try {
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: encryptedData.iv,
        },
        key,
        encryptedData.data
      );

      return decryptedData;
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt content. Key may be invalid or content corrupted.');
    }
  }

  /**
   * Encrypt string content (convenience method)
   */
  async encryptString(content: string, key: CryptoKey): Promise<EncryptedData> {
    const encoder = new TextEncoder();
    const contentBytes = encoder.encode(content);
    return this.encrypt(contentBytes, key);
  }

  /**
   * Decrypt to string (convenience method)
   */
  async decryptString(encryptedData: EncryptedData, key: CryptoKey): Promise<string> {
    const decryptedBytes = await this.decrypt(encryptedData, key);
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBytes);
  }

  /**
   * Generate user-specific salt for PBKDF2
   * Should be stored securely and reused for the same user
   */
  generateSalt(): ArrayBuffer {
    return crypto.getRandomValues(new Uint8Array(16));
  }

  /**
   * Get salt for user (from storage or generate new)
   */
  async getUserSalt(userId: string): Promise<ArrayBuffer> {
    const saltKey = `salt-${userId}`;
    const storedSalt = localStorage.getItem(saltKey);

    if (storedSalt) {
      // Convert base64 to ArrayBuffer
      const binaryString = atob(storedSalt);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    }

    // Generate new salt
    const newSalt = this.generateSalt();

    // Store as base64
    const saltArray = new Uint8Array(newSalt);
    const binaryString = String.fromCharCode(...saltArray);
    const base64Salt = btoa(binaryString);
    localStorage.setItem(saltKey, base64Salt);

    return newSalt;
  }

  /**
   * Clear key cache (call on logout)
   */
  clearKeyCache(): void {
    this.keyCache.clear();
  }

  /**
   * Verify encryption/decryption roundtrip (for testing)
   */
  async testEncryption(key: CryptoKey): Promise<boolean> {
    const testContent = 'Test encryption content';
    try {
      const encrypted = await this.encryptString(testContent, key);
      const decrypted = await this.decryptString(encrypted, key);
      return decrypted === testContent;
    } catch (error) {
      console.error('Encryption test failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const encryptionService = new EncryptionService();
